import { useCallback, useEffect, useRef, useState } from 'react';
import axiosInstant from '../../../../api';
import config from '../../../../config';
import {
  adminAttrNetCategoryGraph,
  adminAttrNetSubProfessionGraph,
  adminAttrNetSubSubProfessionGraph,
  adminAttrNetAttributeValueGraph,
} from '../../../../api/endpoints';
import { AttributeGraphResponse } from './attribute.types';

export type AttributeMode =
  | 'product_category'
  | 'sub_profession'
  | 'sub_sub_profession'
  | 'attribute_value';

const endpointFor = (mode: AttributeMode, id: number): string => {
  if (mode === 'product_category') return adminAttrNetCategoryGraph(id);
  if (mode === 'sub_profession') return adminAttrNetSubProfessionGraph(id);
  if (mode === 'sub_sub_profession') return adminAttrNetSubSubProfessionGraph(id);
  return adminAttrNetAttributeValueGraph(id);
};

// Fetches the focused attribute-network neighborhood for one focus entity.
export function useAttributeGraph(mode: AttributeMode, id: number | null) {
  const [data, setData] = useState<AttributeGraphResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  // Monotonic request sequence: only the latest refetch may commit state —
  // otherwise a slow response for the previous focus can land under the new
  // one and staged edits would save links onto the wrong entity.
  const seqRef = useRef(0);

  const refetch = useCallback(async () => {
    const seq = ++seqRef.current;
    if (!id) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(false);
    try {
      const res = await axiosInstant.get(`${config.API_URL}${endpointFor(mode, id)}`);
      if (seq !== seqRef.current) return;
      setData(res.data);
    } catch (err) {
      if (seq !== seqRef.current) return;
      setError(true);
      setData(null);
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, [mode, id]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { data, loading, error, refetch };
}
